import { notifyAnalytics } from '@/api/analytics/notifyAnalytics'
import directus, { storage } from '@/api/clients/directus-client'
import keycloak from '@/keycloak'
import { readUserPermissions, type ReadUserPermissionsOutput } from '@directus/sdk'

/**
 * API Call to check if current token exists and is valid.
 * This will authenticate the directus connection if successful.
 *
 * @returns `true` if token auth successful `false` if failed.
 */
export async function checkToken(): Promise<boolean> {
  let authenticated = false
  const realm = keycloak.realm

  if (realm) {
    try {
      const response = await fetch(
        'https://auth.powertrip.earth/auth/realms/' + realm + '/directus_sso_sidecar',
        {
          method: 'GET',
          credentials: 'include',
        },
      )

      if (!response.ok) {
        throw new Error(`Fetch error: ${response.status} ${response.statusText}`)
      }

      const res = await response.json()

      if (!res || !res.data) {
        throw new Error('Invalid response data')
      }

      if (typeof res.data !== 'object') {
        throw new Error('Invalid response data type')
      }
      // set custom credentials to the storage
      storage.set(res.data)

      await directus
        .refresh()
        .then((data) => {
          authenticated = true
        })
        .catch((err: unknown) => {
          if (import.meta.env.DEV) console.error('Directus auth error:', err)
          // The user is not authenticated
          keycloak.logout({ redirectUri: window.location.origin })
        })
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error checking token:', error)
    }
  }
  return authenticated
}

/**
 * API Call to logout the user.
 * This will invalidate the directus connection if successful and is a fire and forget style function.
 */
export async function logout(redirectUri?: string) {
  try {
    await directus.logout()
    // check for keycloak session
    if (keycloak.authenticated) {
      await keycloak.logout({
        redirectUri: redirectUri || window.location.origin + '/#/logout/',
      })
    }
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      if (import.meta.env.VITE_VERBOSE_MODE === 'true') console.error('logout failed.', error)
    } else notifyAnalytics({ type: 'log_error', data: error })
  } finally {
    storage.clear() //remove directus tokens
  }
}

export async function fetchPermissions(): Promise<ReadUserPermissionsOutput | undefined> {
  try {
    // check if user is logged in
    const authData = await storage.get()
    // Guard clause: exit if no auth data available to prevent 401 error
    if (Object.keys(authData).length === 0) return undefined
    const res = await directus.request(readUserPermissions())
    return res
  } catch (error) {
    if (import.meta.env.MODE === 'development') {
      if (import.meta.env.VITE_VERBOSE_MODE === 'true')
        console.error('Error fetching permissions:', error)
    } else notifyAnalytics({ type: 'log_error', data: error })
  }
}
